// cspell:ignore INAPP HARMONYOS
import { UAParser, type UAParserExt } from 'ua-parser-js';
import { BrowserName, BrowserType, OSName } from 'ua-parser-js/enums';
import { isNullish } from '@/utils/isNullish';

export type PlatformOs = 'ios' | 'android' | 'desktop';

export type InAppBrowser = 'google' | 'line' | 'facebook' | 'instagram' | 'tiktok' | 'twitter' | 'webview';

export interface Platform {
  os: PlatformOs;
  /** Major version of iOS or Android; `null` when the UA does not carry it (iPadOS, desktop). */
  osMajorVersion: number | null;
  inApp: InAppBrowser | null;
}

const GSA = 'GSA';

// ua-parser-js only reads GSA on iOS; on Android the Google app is left as a plain Chrome WebView.
const GOOGLE_APP_EXTENSION: UAParserExt = {
  browser: [[/\bGSA\/([\w.]+)/i], ['version', ['name', GSA], ['type', BrowserType.INAPP]]],
};

const IN_APP_BY_BROWSER_NAME: Readonly<Record<string, InAppBrowser>> = {
  [GSA]: 'google',
  [BrowserName.LINE]: 'line',
  [BrowserName.FACEBOOK]: 'facebook',
  [BrowserName.INSTAGRAM]: 'instagram',
  [BrowserName.TIKTOK]: 'tiktok',
  [BrowserName.TWITTER]: 'twitter',
};

const toPlatformOs = (osName: string | undefined, maxTouchPoints: number): PlatformOs => {
  if (osName === OSName.IOS) {
    return 'ios';
  }
  // iPadOS Safari reports a desktop Mac UA; only touch support gives it away.
  if (osName === OSName.MACOS && maxTouchPoints > 1) {
    return 'ios';
  }
  // HarmonyOS 2–4 phones run Android apps and send an Android UA with a HarmonyOS token.
  if (osName === OSName.ANDROID || osName === OSName.HARMONYOS) {
    return 'android';
  }
  return 'desktop';
};

const toOsMajorVersion = (osName: string | undefined, osVersion: string | undefined): number | null => {
  // A HarmonyOS UA carries no Android version, and an iPadOS desktop UA carries the macOS one.
  if (osName !== OSName.IOS && osName !== OSName.ANDROID) {
    return null;
  }
  const major = Number.parseInt(osVersion ?? '', 10);
  return Number.isNaN(major) ? null : major;
};

const toInAppBrowser = ({ name, type }: { name?: string; type?: string }, os: PlatformOs): InAppBrowser | null => {
  if (type !== BrowserType.INAPP) {
    return null;
  }
  const known = IN_APP_BY_BROWSER_NAME[name ?? ''];
  if (!isNullish(known)) {
    return known;
  }
  // Any other Android app's WebView; other in-app hosts on iOS stay unflagged, as before.
  return os === 'android' ? 'webview' : null;
};

export const detectPlatform = (
  userAgent: string = navigator.userAgent,
  maxTouchPoints: number = navigator.maxTouchPoints ?? 0
): Platform => {
  const { browser, os } = new UAParser(userAgent, GOOGLE_APP_EXTENSION).getResult();
  const platformOs = toPlatformOs(os.name, maxTouchPoints);
  return {
    os: platformOs,
    osMajorVersion: toOsMajorVersion(os.name, os.version),
    inApp: toInAppBrowser(browser, platformOs),
  };
};
